import { lazy, Suspense, useEffect, useRef, useState, type ReactElement } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useTelegram } from '@/hooks/useTelegram'
import { useThemeSync } from '@/hooks/useThemeSync'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { useNetworkPlanningAccess } from '@/hooks/useNetworkPlanningAccess'
import { useRoyaltyAccess } from '@/hooks/useRoyaltyAccess'
import { useAuthStore, useAppStore } from '@/store'
import { useStudioStore } from '@/store'
import { AuthProvider } from '@/components/AuthProvider'
import { PedagogySectionLayout } from '@/components/pedagogy/PedagogySectionLayout'
import { PedagogyAdminRouteGuard } from '@/components/pedagogy/PedagogyAdminRouteGuard'
import { PedagogyReviewsRoute } from '@/components/pedagogy/PedagogyReviewsRoute'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { resolveStudioAccessState } from '@/lib/studioAccessState'
import { Layout } from '@/components/Layout'
import { MetricsPage } from '@/pages/MetricsPage'
import { HistoryPage } from '@/pages/HistoryPage'
import { AnalyticsPage } from '@/pages/AnalyticsPage'
import { StudioClientLifecyclePage } from '@/pages/StudioClientLifecyclePage'
import { StudioBusinessHealthPage } from '@/pages/StudioBusinessHealthPage'
import { PartnerBusinessHealthPage } from '@/pages/PartnerBusinessHealthPage'
import { RatingsPage } from '@/pages/RatingsPage'
import { GlobalAnalyticsPage } from '@/pages/GlobalAnalyticsPage'
import { GlobalManagementPage } from '@/pages/GlobalManagementPage'
import { GlobalRankingsPage } from '@/pages/GlobalRankingsPage'
import { ManagementCompanyDashboard } from '@/pages/ManagementCompanyDashboard'
import { ManagementAnalyticsPage } from '@/pages/ManagementAnalyticsPage'
import { ManagementRatingsPage } from '@/pages/ManagementRatingsPage'
import { ManagementCompanyPageSimple } from '@/pages/ManagementCompanyPageSimple'
import { InvitePage } from '@/pages/InvitePage'
import { LandingPage } from '@/pages/LandingPage'
import { WebLoginPage } from '@/pages/WebLoginPage'
import { EmailVerifiedPage } from '@/pages/EmailVerifiedPage'
import { NoAccessPage } from '@/pages/NoAccessPage'
import { WeeklyPlanGridPrototype } from '@/pages/WeeklyPlanGridPrototype'
import { UnifiedRatingsPrototype } from '@/pages/UnifiedRatingsPrototype'
import { PlanHistoryPage } from '@/pages/PlanHistoryPage'
import { MonthlyRevenuePage } from '@/pages/MonthlyRevenuePage'
import { NotificationSettingsPage } from '@/pages/NotificationSettingsPage'
import { NotificationAdminPage } from '@/pages/NotificationAdminPage'
import { PartnersPage } from '@/pages/PartnersPage'
import { PartnerAnalyticsPage } from '@/pages/PartnerAnalyticsPage'
import { PartnerMarketingPage } from '@/pages/PartnerMarketingPage'
import { PartnerMarketingDailySummaryPage } from '@/pages/PartnerMarketingDailySummaryPage'
import { PartnerMarketingPlanningPage } from '@/pages/PartnerMarketingPlanningPage'
import { CityAnalyticsPage } from '@/pages/CityAnalyticsPage'
import { TerritorialManagersPage } from '@/pages/TerritorialManagersPage'
import { TerritorialManagerDetailPage } from '@/pages/TerritorialManagerDetailPage'
import { PartnersCoveragePage } from '@/pages/PartnersCoveragePage'
import { PersonalManagersPage } from '@/pages/PersonalManagersPage'
import { PersonalManagerDetailPage } from '@/pages/PersonalManagerDetailPage'
import { PersonalManagersCoveragePage } from '@/pages/PersonalManagersCoveragePage'
import { PersonalManagersControlPage } from '@/pages/PersonalManagersControlPage'
import { PersonalManagersHackathonPage } from '@/pages/PersonalManagersHackathonPage'
import { PedagogySpecialistsPage } from '@/pages/PedagogySpecialistsPage'
import { PedagogySpecialistsCoveragePage } from '@/pages/PedagogySpecialistsCoveragePage'
import { PedagogySpecialistDetailPage } from '@/pages/PedagogySpecialistDetailPage'
import { PedagogyTeachersPage } from '@/pages/PedagogyTeachersPage'
import { PedagogyTeacherCreatePage } from '@/pages/PedagogyTeacherCreatePage'
import { PedagogyLegacyReviewMatchingPage } from '@/pages/PedagogyLegacyReviewMatchingPage'
import { PedagogyTeacherDetailPage } from '@/pages/PedagogyTeacherDetailPage'
import { PedagogyReviewDispatchPage } from '@/pages/PedagogyReviewDispatchPage'
import { PedagogyReviewQueuePage } from '@/pages/PedagogyReviewQueuePage'
import { PedagogyTeacherRoleRequestsPage } from '@/pages/PedagogyTeacherRoleRequestsPage'
import { PedagogyReviewDistributionPage } from '@/pages/PedagogyReviewDistributionPage'
import { ReviewSamplingPage } from '@/pages/ReviewSamplingPage'
import { ShmpMembersPage } from '@/pages/ShmpMembersPage'
import { ShmpDashboardPage } from '@/pages/ShmpDashboardPage'
import { TrainerChecklistPage } from '@/pages/TrainerChecklistPage'
import { TrainerChecklistReportPage } from '@/pages/TrainerChecklistReportPage'
import { TrainerChecklistSummaryPage } from '@/pages/TrainerChecklistSummaryPage'
import { PartnerTeachersPage } from '@/pages/PartnerTeachersPage'
import { PartnerTeacherDetailPage } from '@/pages/PartnerTeacherDetailPage'
import { PartnerTeacherAnalyticsDetailPage } from '@/pages/PartnerTeacherAnalyticsDetailPage'
import { PartnerTrainerReviewPage } from '@/pages/PartnerTrainerReviewPage'
import { StudioClosurePreparationsPage } from '@/pages/StudioClosurePreparationsPage'
import { CrmAccountTransfersPage } from '@/pages/CrmAccountTransfersPage'
import { MissingPaymentsControlPage } from '@/pages/MissingPaymentsControlPage'
import { OriginalHistoryPage } from '@/pages/OriginalHistoryPage'
import { OriginalCasesPage } from '@/pages/OriginalCasesPage'
import { LegalEntityOwnersPage } from '@/pages/LegalEntityOwnersPage'
import { DocumentsRegistryPage } from '@/pages/DocumentsRegistryPage'
import { DocumentsRegistryDetailPage } from '@/pages/DocumentsRegistryDetailPage'
import { ManagementHubPage } from '@/pages/ManagementHubPage'
import { ContractRunbookSafetyPage } from '@/pages/ContractRunbookSafetyPage'
import { EmployeeActivityDashboard } from '@/pages/EmployeeActivityDashboard'
import { ManagementUsersPage } from '@/pages/ManagementUsersPage'
import { ManagementAccessPage } from '@/pages/ManagementAccessPage'
import { TeamWorkBoardPage } from '@/pages/TeamWorkBoardPage'
import { ManagementUsersAuthLogPage } from '@/pages/ManagementUsersAuthLogPage'
import { AdsAccountMappingsPage } from '@/pages/AdsAccountMappingsPage'
import { LoadingScreen } from '@/components/LoadingScreen'
import { getStartParam } from '@/lib/getStartParam'
import { MAINTENANCE_BYPASS_START_PARAM } from '@/lib/maintenanceWindow'
import { pathToScreenName, logScreenVisit } from '@/lib/appTracking'
import { resetRoyaltyNavigationClock } from '@/lib/royaltyPerformance'
import type { RoyaltyPermission } from '@/types'

// Игра — публичный учебный симулятор экономики студии (маршрут /game).
// Отдельным чанком: в основной бандл приложения ей попадать незачем.
const GamePage = lazy(() => import('@/pages/GamePage').then((m) => ({ default: m.GamePage })))
const RoyaltiesPage = lazy(() => import('@/pages/RoyaltiesPage').then((m) => ({ default: m.RoyaltiesPage })))
const RoyaltyHubPage = lazy(() => import('@/pages/RoyaltyHubPage').then((m) => ({ default: m.RoyaltyHubPage })))
const RoyaltyAccrualProcessPage = lazy(() => import('@/pages/RoyaltyAccrualProcessPage').then((m) => ({ default: m.RoyaltyAccrualProcessPage })))
const PartnerRoyaltiesPage = lazy(() => import('@/pages/PartnerRoyaltiesPage').then((m) => ({ default: m.PartnerRoyaltiesPage })))
const StudioRoyaltiesPage = lazy(() => import('@/pages/StudioRoyaltiesPage').then((m) => ({ default: m.StudioRoyaltiesPage })))
const RoyaltyContractPage = lazy(() => import('@/pages/RoyaltyContractPage').then((m) => ({ default: m.RoyaltyContractPage })))
const RoyaltyImportPage = lazy(() => import('@/pages/RoyaltyImportPage').then((m) => ({ default: m.RoyaltyImportPage })))
const RoyaltyUnallocatedPage = lazy(() => import('@/pages/RoyaltyUnallocatedPage').then((m) => ({ default: m.RoyaltyUnallocatedPage })))
const RoyaltyAllocationCategoryPage = lazy(() => import('@/pages/RoyaltyAllocationCategoryPage').then((m) => ({ default: m.RoyaltyAllocationCategoryPage })))
const RoyaltyRefundsPage = lazy(() => import('@/pages/RoyaltyRefundsPage').then((m) => ({ default: m.RoyaltyRefundsPage })))
const RoyaltyCoverageTimelinePage = lazy(() => import('@/pages/RoyaltyCoverageTimelinePage').then((m) => ({ default: m.RoyaltyCoverageTimelinePage })))
const RoyaltyFxExceptionsPage = lazy(() => import('@/pages/RoyaltyFxExceptionsPage').then((m) => ({ default: m.RoyaltyFxExceptionsPage })))
const NetworkPlanningPage = lazy(() => import('@/pages/NetworkPlanningPage').then((m) => ({ default: m.NetworkPlanningPage })))
const NetworkPlanningEditorPage = lazy(() => import('@/pages/NetworkPlanningEditorPage').then((m) => ({ default: m.NetworkPlanningEditorPage })))
const ManagementHealthPage = lazy(() => import('@/pages/ManagementHealthPage').then((m) => ({ default: m.ManagementHealthPage })))

function LazyRoute({ children }: { children: ReactElement }) {
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
}

// Получаем start_param ДО монтирования роутера
const INITIAL_START_PARAM = getStartParam()
console.log('🎯 App: INITIAL_START_PARAM captured before router mount:', INITIAL_START_PARAM)

// Автоматически логирует переходы между экранами и время проведённое на каждом
function RouteTracker() {
  const location = useLocation()
  const user = useAuthStore(s => s.user)
  const prevRef = useRef({ pathname: location.pathname, search: location.search, time: Date.now() })

  useEffect(() => {
    const { pathname: prevPathname, search: prevSearch, time: prevTime } = prevRef.current
    const durationSec = Math.round((Date.now() - prevTime) / 1000)
    if ((prevPathname !== location.pathname || prevSearch !== location.search) && location.pathname.startsWith('/royalties')) {
      resetRoyaltyNavigationClock()
    }
    if (user?.id && (prevPathname !== location.pathname || prevSearch !== location.search)) {
      logScreenVisit(user.id, pathToScreenName(prevPathname), durationSec, prevPathname, prevSearch)
    }
    prevRef.current = { pathname: location.pathname, search: location.search, time: Date.now() }
  }, [location.pathname, location.search, user?.id])

  return null
}

// Служебные start_param — не токены инвайта: сброс кэша и обход заглушки технических работ (MaintenanceGate).
function isServiceStartParam(param: string): boolean {
  return param === 'clear' || param === MAINTENANCE_BYPASS_START_PARAM
}

// Компонент для обработки start_param
function StartParamHandler() {
  const navigate = useNavigate()
  const location = useLocation()
  const hasProcessedRef = useRef(false)
  const processedParamRef = useRef<string | null>(null)

  useEffect(() => {
    // Проверяем, не находимся ли мы уже на странице инвайта
    if (location.pathname.startsWith('/invite/')) {
      console.log('🔍 StartParamHandler: already on invite page, skipping redirect')
      hasProcessedRef.current = true
      processedParamRef.current = INITIAL_START_PARAM
      return
    }

    // Обрабатываем параметр только один раз
    if (hasProcessedRef.current) {
      console.log('🔍 StartParamHandler: already processed, skipping')
      return
    }

    console.log('🔍 StartParamHandler: processing INITIAL_START_PARAM:', INITIAL_START_PARAM)
    console.log('🔍 StartParamHandler: current pathname:', location.pathname)

    if (INITIAL_START_PARAM && !isServiceStartParam(INITIAL_START_PARAM)) {
      console.log('🎯 StartParamHandler: redirecting to invite page with token:', INITIAL_START_PARAM)
      hasProcessedRef.current = true
      processedParamRef.current = INITIAL_START_PARAM
      navigate(`/invite/${INITIAL_START_PARAM}`, { replace: true })
    } else {
      console.log('🔍 StartParamHandler: no valid start_param found, continuing normal flow')
      hasProcessedRef.current = true
    }
  }, [navigate, location.pathname])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      const freshParam = getStartParam()
      if (!freshParam || isServiceStartParam(freshParam)) return

      if (freshParam !== processedParamRef.current) {
        console.log('🎯 StartParamHandler: new start_param detected on visibility change:', freshParam)
        processedParamRef.current = freshParam
        navigate(`/invite/${freshParam}`, { replace: true })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [navigate])

  return null
}

function StudioAccessRoute({ children }: { children: ReactElement }) {
  const { studios, studiosSyncStatus } = useStudioStore()
  const location = useLocation()
  const accessState = resolveStudioAccessState(studios.length, studiosSyncStatus)

  if (accessState === 'pending') return <LoadingScreen />
  if (accessState === 'denied' || accessState === 'error') {
    return <Navigate to="/no-access" replace />
  }

  const isSeniorStudio = (studio: (typeof studios)[number]) =>
    studio.access_kind === 'senior_teacher' || studio.role === 'senior_teacher'
  const seniorOnlyAccount = studios.length > 0 && studios.every(isSeniorStudio)
  const teacherOnlyAccount = studios.length > 0 && studios.every(studio => studio.role === 'teacher')

  // An ordinary teacher may have other studio rows in the account (for
  // example, after an old invite or a mixed-access response). Restrict every
  // URL belonging to a teacher studio, not only accounts consisting solely of
  // teacher studios. Senior access is an overlay and is handled separately.
  const teacherStudioMatch = location.pathname.match(/^\/partners\/(\d+)\/studios\/(\d+)(?:\/|$)/)
  if (teacherStudioMatch) {
    const teacherStudio = studios.find(studio =>
      Number(studio.partner_number) === Number(teacherStudioMatch[1])
      && Number(studio.studio_internal_id) === Number(teacherStudioMatch[2])
      && studio.role === 'teacher'
      && !isSeniorStudio(studio)
    )
    if (teacherStudio && !new RegExp(
      `^/partners/${teacherStudioMatch[1]}/studios/${teacherStudioMatch[2]}/teachers$`
    ).test(location.pathname)) {
      return <Navigate to={`/partners/${teacherStudioMatch[1]}/studios/${teacherStudioMatch[2]}/teachers`} replace />
    }
  }

  if (seniorOnlyAccount || teacherOnlyAccount) {
    const isTeacherPedagogyPath = studios.some((studio) => {
      const partnerNumber = Number(studio.partner_number)
      const studioId = studio.studio_internal_id
      const pattern = teacherOnlyAccount
        ? `^/partners/${partnerNumber}/studios/${studioId}/teachers$`
        : `^/partners/${partnerNumber}/studios/${studioId}/(teachers(?:/.*)?|trainer-reviews)$`
      return new RegExp(pattern).test(location.pathname)
    })
    const isNeutralEntryPath = location.pathname === '/' || location.pathname === '/partners'
    if (!isTeacherPedagogyPath && !isNeutralEntryPath) {
      const first = studios[0]
      return <Navigate to={`/partners/${first.partner_number}/studios/${first.studio_internal_id}/teachers`} replace />
    }
  }

  const partnerMatch = location.pathname.match(/^\/partners\/(\d+)(?:\/|$)/)
  if (partnerMatch) {
    const partnerNumber = Number(partnerMatch[1])
    const partnerStudios = studios.filter(s => Number(s.partner_number) === partnerNumber)
    const seniorStudios = partnerStudios.filter(isSeniorStudio)
    const broaderStudios = partnerStudios.filter(s => !isSeniorStudio(s) && s.role !== 'teacher')
    const teacherStudios = partnerStudios.filter(s => s.role === 'teacher')

    if ((seniorStudios.length > 0 || teacherStudios.length > 0) && broaderStudios.length === 0) {
      const allowedSeniorPath = [...seniorStudios, ...teacherStudios].some((studio) =>
        new RegExp(teacherStudios.includes(studio)
          ? `^/partners/${partnerNumber}/studios/${studio.studio_internal_id}/teachers$`
          : `^/partners/${partnerNumber}/studios/${studio.studio_internal_id}/(teachers(?:/.*)?|trainer-reviews)$`).test(location.pathname)
      )
      if (!allowedSeniorPath) {
        return <Navigate to={`/partners/${partnerNumber}/studios/${seniorStudios[0].studio_internal_id}/teachers`} replace />
      }
    }
  }

  return children
}

function PartnerMarketingAccessRoute({ children }: { children: ReactElement }) {
  const { partnerNumber } = useParams<{ partnerNumber?: string }>()
  const { studios, studiosSyncStatus } = useStudioStore()
  const accessState = resolveStudioAccessState(studios.length, studiosSyncStatus)
  const parsedPartnerNumber = Number(partnerNumber)

  if (accessState === 'pending') return <LoadingScreen />
  if (!Number.isFinite(parsedPartnerNumber)) return <Navigate to="/partners" replace />

  const canAccessMarketing = studios.some((studio) => (
    Number(studio.partner_number) === parsedPartnerNumber
      && (studio.role === 'owner' || studio.role === 'manager' || studio.access_kind === 'management_company')
  ))

  return canAccessMarketing
    ? children
    : <Navigate to={`/partners/${parsedPartnerNumber}/analytics`} replace />
}

function AuthenticatedHomeRedirect() {
  const { studios, selectedPartnerNumber, studiosSyncStatus } = useStudioStore()
  const accessState = resolveStudioAccessState(studios.length, studiosSyncStatus)
  const normalizePartnerNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  if (accessState === 'pending') {
    return <LoadingScreen />
  }

  if (accessState === 'denied' || accessState === 'error') {
    return <Navigate to="/no-access" replace />
  }

  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  if (isManagementCompany) {
    return <Navigate to="/partners" replace />
  }

  const partnerFromStore = selectedPartnerNumber
  const partnerFromStudios = studios.find(
    (studio) => normalizePartnerNumber(studio.partner_number) !== null
  )?.partner_number
  const targetPartnerNumber = partnerFromStore ?? partnerFromStudios

  if (targetPartnerNumber !== null && targetPartnerNumber !== undefined) {
    const parsedPartnerNumber = normalizePartnerNumber(targetPartnerNumber)
    const partnerStudios = parsedPartnerNumber === null
      ? []
      : studios.filter(
          (studio) => normalizePartnerNumber(studio.partner_number) === parsedPartnerNumber
        )

    const partnerIsSeniorOnly = partnerStudios.length > 0 && partnerStudios.every(
      (studio) => studio.access_kind === 'senior_teacher' || studio.role === 'senior_teacher'
    )
    if (partnerIsSeniorOnly && parsedPartnerNumber !== null) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/studios/${partnerStudios[0].studio_internal_id}/teachers`} replace />
    }

    const partnerIsTeacherOnly = partnerStudios.length > 0 && partnerStudios.every(
      (studio) => studio.role === 'teacher'
    )
    if (partnerIsTeacherOnly && parsedPartnerNumber !== null) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/studios/${partnerStudios[0].studio_internal_id}/teachers`} replace />
    }

    const partnerHasOwner = partnerStudios.some(
      (studio) => studio.role === 'owner' || studio.access_kind === 'owner'
    )
    const partnerHasManagerPedagogy = partnerStudios.find(
      (studio) => studio.role === 'manager'
    )
    if (partnerHasManagerPedagogy && parsedPartnerNumber !== null) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/studios/${partnerHasManagerPedagogy.studio_internal_id}/teachers`} replace />
    }
    if (!partnerHasOwner && partnerStudios.length > 0 && parsedPartnerNumber !== null) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/studios/${partnerStudios[0].studio_internal_id}/metrics`} replace />
    }

    if (parsedPartnerNumber !== null) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/analytics`} replace />
    }
  }

  return <Navigate to="/partners" replace />
}

function PartnerStudiosRedirect() {
  const location = useLocation()
  const { studios } = useStudioStore()
  const match = location.pathname.match(/^\/partners\/([^/]+)\/studios$/)
  const partnerNumber = match?.[1]

  if (!partnerNumber) return <Navigate to="/partners" replace />

  const partnerStudios = studios.filter(s => String(s.partner_number) === partnerNumber)
  if (partnerStudios.length > 0 && partnerStudios.every(s => s.access_kind === 'senior_teacher' || s.role === 'senior_teacher' || s.role === 'teacher')) {
    return <Navigate to={`/partners/${partnerNumber}/studios/${partnerStudios[0].studio_internal_id}/teachers`} replace />
  }

  return <Navigate to={`/partners/${partnerNumber}/analytics`} replace />
}

/**
 * Редирект со старого адреса педагогического раздела на канонический.
 *
 * Сохраняет query-строку и хэш: у формы отсмотра всё состояние входа живёт
 * именно в них (`?reviewId=`, `?draft=1`, `?teacherId=`), и потеря query
 * превратила бы открытие существующего отсмотра в создание нового.
 */
function PedagogyLegacyRedirect({
  to,
}: {
  to: string | ((params: Readonly<Record<string, string | undefined>>) => string)
}) {
  const params = useParams()
  const location = useLocation()
  const target = typeof to === 'function' ? to(params) : to

  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}

/**
 * Защитный роут для карточки педагога.
 *
 * В норме React Router выбирает статические `teachers/new` и
 * `teachers/legacy-reviews` раньше динамического `teachers/:teacherId`.
 * Но этот guard не позволяет служебным slug раздела когда-либо попасть
 * в PedagogyTeacherDetailPage как teacherId (например, после частичного
 * мержа роутов или при отличающейся версии router в хост-приложении).
 */
function PedagogyTeacherRoute() {
  const { teacherId } = useParams<{ teacherId?: string }>()

  if (teacherId === 'legacy-reviews') {
    return <PedagogyLegacyReviewMatchingPage />
  }

  if (teacherId === 'new') {
    return <PedagogyTeacherCreatePage />
  }

  return <PedagogyTeacherDetailPage />
}

function StudioMetricsRedirect() {
  const { partnerNumber, studioId } = useParams<{ partnerNumber?: string; studioId?: string }>()
  const location = useLocation()

  if (!partnerNumber || !studioId) {
    return <Navigate to="/partners" replace />
  }

  return <Navigate to={`/partners/${partnerNumber}/studios/${studioId}/studio-metrics-grid`} replace state={location.state} />
}

function AnalyticsEntryRedirect() {
  const { studios, selectedPartnerNumber } = useStudioStore()
  const normalizePartnerNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  if (isManagementCompany) {
    return <GlobalAnalyticsPage />
  }

  const partnerFromStore = selectedPartnerNumber
  const partnerFromStudios = studios.find(
    (studio) => normalizePartnerNumber(studio.partner_number) !== null
  )?.partner_number
  const targetPartnerNumber = partnerFromStore ?? partnerFromStudios

  if (targetPartnerNumber !== null && targetPartnerNumber !== undefined) {
    const parsedPartnerNumber = normalizePartnerNumber(targetPartnerNumber)
    const partnerStudios = parsedPartnerNumber === null
      ? []
      : studios.filter(
          (studio) => normalizePartnerNumber(studio.partner_number) === parsedPartnerNumber
        )

    // Teachers and senior teachers do not have an analytics entry point.
    // Keep this redirect local as well as in StudioAccessRoute so a stale
    // /analytics bookmark cannot mount the analytics page first.
    const teacherOnlyPartner = partnerStudios.length > 0 && partnerStudios.every(
      (studio) => studio.role === 'teacher'
        || studio.role === 'senior_teacher'
        || studio.access_kind === 'senior_teacher'
    )
    if (teacherOnlyPartner && parsedPartnerNumber !== null) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/studios/${partnerStudios[0].studio_internal_id}/teachers`} replace />
    }

    if (parsedPartnerNumber !== null && partnerStudios.length === 1) {
      return <Navigate to={`/partners/${parsedPartnerNumber}/studios/${partnerStudios[0].studio_internal_id}/analytics`} replace />
    }

    return <Navigate to={`/partners/${targetPartnerNumber}/analytics`} replace />
  }

  return <Navigate to="/partners" replace />
}

function ManagementEntryRedirect() {
  const { studios, selectedPartnerNumber } = useStudioStore()
  const normalizePartnerNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  if (isManagementCompany) {
    return <Navigate to="/management-dashboard" replace />
  }

  const partnerFromStore = selectedPartnerNumber
  const partnerFromStudios = studios.find(
    (studio) => normalizePartnerNumber(studio.partner_number) !== null
  )?.partner_number
  const targetPartnerNumber = normalizePartnerNumber(partnerFromStore ?? partnerFromStudios)

  if (targetPartnerNumber !== null) {
    return <Navigate to={`/partners/${targetPartnerNumber}/management`} replace />
  }

  return <Navigate to="/partners" replace />
}

function AdvancedManagementCompanyRoute({ children }: { children: ReactElement }) {
  const { user } = useAuthStore()
  const { studios } = useStudioStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const [accessLevel, setAccessLevel] = useState<'standard' | 'advanced' | null | undefined>(undefined)

  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  useEffect(() => {
    let cancelled = false

    if (!user?.id || !isManagementCompany) {
      setAccessLevel(null)
      return
    }

    setAccessLevel(undefined)
    getAccessLevel().then((level) => {
      if (!cancelled) setAccessLevel(level)
    })

    return () => {
      cancelled = true
    }
  }, [user?.id, isManagementCompany, getAccessLevel])

  if (!isManagementCompany) {
    return <Navigate to="/" replace />
  }

  if (accessLevel === undefined) {
    return <LoadingScreen />
  }

  if (accessLevel !== 'advanced') {
    return <Navigate to="/management-dashboard" replace />
  }

  return children
}

/**
 * Раздел `/management/users` — только app-level супер-админ
 * (`management_company_access.is_super_admin`), не любой advanced УК: там контакты,
 * способы входа и журнал попыток авторизации всей сети.
 * Серверные гейты — `public.is_super_admin()` в RPC раздела и в RLS-политиках
 * `auth_request_log` / `app_events`; этот маршрут только не пускает в пустой экран.
 */
function SuperAdminRoute({ children }: { children: ReactElement }) {
  const { studios } = useStudioStore()
  const { isSuperAdmin } = useSuperAdmin()

  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  if (!isManagementCompany) {
    return <Navigate to="/" replace />
  }

  if (isSuperAdmin === undefined) {
    return <LoadingScreen />
  }

  if (!isSuperAdmin) {
    return <Navigate to="/management-dashboard" replace />
  }

  return children
}

function NetworkPlanningRoute({ children }: { children: ReactElement }) {
  const { studios } = useStudioStore()
  const { canViewNetworkPlanning } = useNetworkPlanningAccess()
  const isManagementCompany = studios.some(
    studio => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  if (!isManagementCompany) return <Navigate to="/" replace />
  if (canViewNetworkPlanning === undefined) return <LoadingScreen />
  if (!canViewNetworkPlanning) return <Navigate to="/management-dashboard" replace />
  return children
}

/**
 * Раздел `/management/work` — любой сотрудник УК: заказать доработку и видеть, что с ней.
 * Менять карточки может только участник команды (`work_board_members`) — это решает сервер.
 */
function WorkBoardRoute({ children }: { children: ReactElement }) {
  const { studios } = useStudioStore()
  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )
  if (!isManagementCompany) {
    return <Navigate to="/" replace />
  }
  return children
}

function RoyaltyRoute({
  children,
  permission = 'view',
}: {
  children: ReactElement
  permission?: RoyaltyPermission
}) {
  const { user } = useAuthStore()
  const { studios } = useStudioStore()
  const { loading, hasPermission } = useRoyaltyAccess(user?.id)

  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  if (!isManagementCompany) {
    return <Navigate to="/" replace />
  }

  if (loading) {
    return <LoadingScreen />
  }

  if (!hasPermission(permission)) {
    return <Navigate to="/management-dashboard" replace />
  }

  return children
}

function App() {
  const { isReady, isInTelegram } = useTelegram()
  const { isAuthenticated } = useAuthStore()
  const { setOnline } = useAppStore()
  const hasAuthToken = !!localStorage.getItem('auth_token')

  // Тема: слушатели платформы + хром вокруг приложения. Вызывается ДО раннего
  // возврата LoadingScreen — иначе экран загрузки остаётся вне синхронизации.
  useThemeSync()

  console.log('🏠 App render - isReady:', isReady, 'isAuthenticated:', isAuthenticated, 'isInTelegram:', isInTelegram)
  console.log('🏠 App current path:', window.location.pathname)
  console.log('🏠 App auth token exists:', !!localStorage.getItem('auth_token'))

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [setOnline])

  if (!isReady) {
    console.log('🔄 Showing LoadingScreen - isReady:', isReady)
    return <LoadingScreen />
  }

  console.log('🏠 App routing - isAuthenticated:', isAuthenticated)

  return (
    <Router>
      <StartParamHandler />
      <RouteTracker />
      <AuthProvider>
        <Routes>
          {/* Публичные роуты - доступны всегда */}
          <Route path="/invite/:token" element={<InvitePage />} />

          {/* В Telegram никогда не показываем web login экран */}
          <Route path="/login" element={isInTelegram ? <Navigate to="/" replace /> : <WebLoginPage />} />
          <Route path="/email-verified" element={<EmailVerifiedPage />} />

          {/* Учебный симулятор экономики студии. Публичный: играют и кандидаты
              во франчайзи, у которых доступа в приложение ещё нет. Ничего не
              читает и не пишет в базу — партия живёт в localStorage. */}
          <Route path="/game" element={<Suspense fallback={<LoadingScreen />}><GamePage /></Suspense>} />

          {isAuthenticated ? (
            <>
              <Route path="/no-access" element={<NoAccessPage />} />
              <Route path="/" element={<StudioAccessRoute><Layout /></StudioAccessRoute>}>
                <Route index element={<AuthenticatedHomeRedirect />} />
                <Route path="/studios" element={<Navigate to="/" replace />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/:partnerNumber/analytics" element={<PartnerAnalyticsPage />} />
                <Route path="/partners/:partnerNumber/business-health" element={<PartnerBusinessHealthPage />} />
                <Route path="/partners/:partnerNumber/marketing" element={<PartnerMarketingAccessRoute><PartnerMarketingPage /></PartnerMarketingAccessRoute>} />
                <Route path="/partners/:partnerNumber/marketing/daily" element={<PartnerMarketingAccessRoute><PartnerMarketingDailySummaryPage /></PartnerMarketingAccessRoute>} />
                <Route path="/partners/:partnerNumber/marketing/plan" element={<PartnerMarketingAccessRoute><PartnerMarketingPlanningPage /></PartnerMarketingAccessRoute>} />
                <Route path="/partners/:partnerNumber/cities/:cityId/analytics" element={<CityAnalyticsPage />} />
                <Route path="/partners/:partnerNumber/studios" element={<PartnerStudiosRedirect />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/metrics" element={<MetricsPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/history" element={<HistoryPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/finance" element={<MonthlyRevenuePage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/analytics" element={<AnalyticsPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/clients" element={<StudioClientLifecyclePage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/business-health" element={<StudioBusinessHealthPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/marketing" element={<PartnerMarketingAccessRoute><PartnerMarketingPage /></PartnerMarketingAccessRoute>} />
                <Route path="/partners/:partnerNumber/studios/:studioId/marketing/daily" element={<PartnerMarketingAccessRoute><PartnerMarketingDailySummaryPage /></PartnerMarketingAccessRoute>} />
                <Route path="/partners/:partnerNumber/studios/:studioId/marketing/plan" element={<PartnerMarketingAccessRoute><PartnerMarketingPlanningPage /></PartnerMarketingAccessRoute>} />
                <Route path="/partners/:partnerNumber/studios/:studioId/ratings" element={<RatingsPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/teachers" element={<PartnerTeachersPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/teachers/reviews/new" element={<TrainerChecklistPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/teachers/analytics/:teacherId" element={<PartnerTeacherAnalyticsDetailPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/teachers/:teacherId" element={<PartnerTeacherDetailPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/teachers/:teacherId/reviews/:reviewKey" element={<PartnerTrainerReviewPage />} />
                {/* Старый адрес «Отсмотров» оставляем рабочим для закладок и старых ссылок. */}
                <Route path="/partners/:partnerNumber/studios/:studioId/trainer-reviews" element={<PartnerTeachersPage />} />
                <Route path="/analytics" element={<AnalyticsEntryRedirect />} />
                <Route path="/rankings" element={<GlobalRankingsPage />} />
                <Route path="/management" element={<ManagementEntryRedirect />} />
                <Route path="/partners/:partnerNumber/management" element={<GlobalManagementPage />} />
                <Route path="/management-dashboard" element={<ManagementCompanyDashboard />} />
                <Route path="/management-analytics" element={<ManagementAnalyticsPage />} />
                <Route path="/management-ratings" element={<UnifiedRatingsPrototype />} />
                <Route path="/management-ratings/subscriptions" element={<ManagementRatingsPage />} />
                <Route path="/management-ratings/prototype" element={<Navigate to="/management-ratings" replace />} />
                <Route path="/management/settings" element={<ManagementCompanyPageSimple />} />
                {/* прежний адрес экрана настроек УК — сохраняем закладки и старые ссылки */}
                <Route path="/management-users" element={<Navigate to="/management/settings" replace />} />
                <Route path="/management/territorial-managers" element={<TerritorialManagersPage />} />
                <Route path="/management/territorial-managers/:userId" element={<TerritorialManagerDetailPage />} />
                <Route path="/management/personal-managers" element={<PersonalManagersPage />} />
                <Route path="/management/personal-managers-hackathon" element={<PersonalManagersHackathonPage />} />
                <Route path="/management/personal-managers-coverage" element={<PersonalManagersCoveragePage />} />
                <Route path="/management/personal-managers-control" element={<PersonalManagersControlPage />} />
                <Route path="/management/personal-managers/:userId" element={<PersonalManagerDetailPage />} />
                {/*
                  Педагогический раздел: один корень — один гейт, одна навигация,
                  один резолв доступа. Пути объявлены в src/lib/pedagogyRoutes.ts.
                */}
                <Route path="/management/pedagogy" element={<PedagogySectionLayout />}>
                  <Route index element={<Navigate to="department" replace />} />
                  <Route path="teachers" element={<PedagogyTeachersPage />} />
                  <Route path="teachers/review-distribution" element={<PedagogyReviewDistributionPage />} />
                  <Route path="teachers/new" element={<PedagogyTeacherCreatePage />} />
                  <Route path="teachers/legacy-reviews" element={<PedagogyLegacyReviewMatchingPage />} />
                  <Route path="teachers/:teacherId" element={<PedagogyTeacherRoute />} />
                  <Route path="reviews" element={<PedagogyReviewsRoute><TrainerChecklistSummaryPage /></PedagogyReviewsRoute>} />
                  <Route path="reviews/new" element={<TrainerChecklistPage />} />
                  <Route path="reviews/report/:reviewId" element={<TrainerChecklistReportPage />} />
                  <Route path="queue" element={<PedagogyReviewQueuePage />} />
                  <Route path="role-requests" element={<PedagogyTeacherRoleRequestsPage />} />
                  <Route path="department/planning" element={<PedagogyAdminRouteGuard><PedagogyReviewDispatchPage /></PedagogyAdminRouteGuard>} />
                  <Route path="department" element={<PedagogySpecialistsPage />} />
                  <Route path="department/coverage" element={<PedagogyAdminRouteGuard><PedagogySpecialistsCoveragePage /></PedagogyAdminRouteGuard>} />
                  <Route path="department/:specialistUserId" element={<PedagogyAdminRouteGuard allowOwnSpecialistPage><PedagogySpecialistDetailPage /></PedagogyAdminRouteGuard>} />
                  {/* Опечатка в адресе внутри раздела оставляет в разделе, а не выбрасывает на главную */}
                  <Route path="*" element={<Navigate to={pedagogyPaths.department} replace />} />
                </Route>
                {/* Прежние адреса раздела — сохраняем закладки, ссылки из чатов и историю */}
                <Route path="/management/pedagogy-teachers" element={<PedagogyLegacyRedirect to={pedagogyPaths.teachers} />} />
                <Route path="/management/pedagogy-specialists" element={<PedagogyLegacyRedirect to={pedagogyPaths.department} />} />
                <Route path="/management/pedagogy-specialists/:specialistUserId" element={<PedagogyLegacyRedirect to={params => pedagogyPaths.specialist(params.specialistUserId ?? '')} />} />
                <Route path="/management/pedagogy-specialists-coverage" element={<PedagogyLegacyRedirect to={pedagogyPaths.coverage} />} />
                <Route path="/management/studio-closures" element={<AdvancedManagementCompanyRoute><StudioClosurePreparationsPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/crm-accounts" element={<AdvancedManagementCompanyRoute><CrmAccountTransfersPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/ads-account-mappings" element={<AdvancedManagementCompanyRoute><AdsAccountMappingsPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/missing-payments" element={<AdvancedManagementCompanyRoute><MissingPaymentsControlPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/original" element={<AdvancedManagementCompanyRoute><OriginalHistoryPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/original/cases" element={<SuperAdminRoute><OriginalCasesPage /></SuperAdminRoute>} />
          <Route path="/management/legal-entities" element={<AdvancedManagementCompanyRoute><LegalEntityOwnersPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/contract-runbook-safety" element={<AdvancedManagementCompanyRoute><ContractRunbookSafetyPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/partners-coverage" element={<PartnersCoveragePage />} />
                <Route path="/management/hub" element={<ManagementHubPage />} />
                <Route path="/management/network-planning" element={<NetworkPlanningRoute><LazyRoute><NetworkPlanningPage /></LazyRoute></NetworkPlanningRoute>} />
                <Route path="/management/network-planning/edit" element={<NetworkPlanningRoute><LazyRoute><NetworkPlanningEditorPage /></LazyRoute></NetworkPlanningRoute>} />
                {/* Заявка на доступ в УК — открыта сотруднику УК ЛЮБОГО уровня: у standard
                    кнопок не было никогда, ради него заявка и заведена. Выдаёт доступ
                    супер-админ в /management/users; серверный гейт — management_access_actor(). */}
                <Route path="/management/access" element={<ManagementAccessPage />} />
                {/* Доска команды: открыта любому сотруднику УК, гейт внутри страницы и в RPC */}
                <Route path="/management/work" element={<WorkBoardRoute><TeamWorkBoardPage /></WorkBoardRoute>} />
                <Route path="/management/review-sampling" element={<SuperAdminRoute><ReviewSamplingPage /></SuperAdminRoute>} />
                <Route path="/management/review-sampling" element={<SuperAdminRoute><ReviewSamplingPage /></SuperAdminRoute>} />
                <Route path="/management/users" element={<SuperAdminRoute><ManagementUsersPage /></SuperAdminRoute>} />
                <Route path="/management/users/auth-attempts" element={<SuperAdminRoute><ManagementUsersAuthLogPage /></SuperAdminRoute>} />
                <Route path="/management/users/activity" element={<SuperAdminRoute><EmployeeActivityDashboard /></SuperAdminRoute>} />
                {/* Здоровье базы и ошибки клиента; серверный гейт — is_super_admin() в get_ops_health_dashboard. */}
                <Route path="/management/health" element={<SuperAdminRoute><LazyRoute><ManagementHealthPage /></LazyRoute></SuperAdminRoute>} />
                {/* старый адрес экрана активности — сохраняем ссылки из закладок и истории */}
                <Route path="/management/employee-activity" element={<Navigate to="/management/users/activity" replace />} />
                {/* Сам реестр открыт всем: advanced получает расширенный экран, остальные — витрину (развилка внутри DocumentsRegistryPage) */}
                <Route path="/management/documents-registry" element={<DocumentsRegistryPage />} />
                {/* Карточка договора — только advanced: с витрины провала в неё нет, но прямая ссылка обошла бы это */}
                <Route path="/management/documents-registry/:contractId" element={<AdvancedManagementCompanyRoute><DocumentsRegistryDetailPage /></AdvancedManagementCompanyRoute>} />
                <Route path="/management/shmp" element={<ShmpDashboardPage />} />
                <Route path="/management/shmp/members" element={<ShmpMembersPage />} />
                {/*
                  Прежние адреса отсмотров. Базовым URL подраздела была ФОРМА создания,
                  а список висел дочерним /summary — заход на «очевидный» адрес начинал
                  новый отсмотр и затирал единственный черновик в localStorage.
                */}
                <Route path="/management/trainer-checklist/summary" element={<PedagogyLegacyRedirect to={pedagogyPaths.reviews} />} />
                <Route path="/management/trainer-checklist/report/:reviewId" element={<PedagogyLegacyRedirect to={params => pedagogyPaths.reviewReport(params.reviewId ?? '')} />} />
                <Route path="/management/trainer-checklist" element={<PedagogyLegacyRedirect to={pedagogyPaths.reviewNew} />} />
                <Route path="/royalties" element={<RoyaltyRoute><LazyRoute><RoyaltyHubPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/all" element={<RoyaltyRoute><LazyRoute><RoyaltiesPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/coverage-timeline" element={<RoyaltyRoute><LazyRoute><RoyaltyCoverageTimelinePage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/fx-exceptions" element={<RoyaltyRoute><LazyRoute><RoyaltyFxExceptionsPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/process" element={<RoyaltyRoute><LazyRoute><RoyaltyAccrualProcessPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/partner/:partnerNumber" element={<RoyaltyRoute><LazyRoute><PartnerRoyaltiesPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/series/:seriesId" element={<RoyaltyRoute><LazyRoute><StudioRoyaltiesPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/contract/:contractId" element={<RoyaltyRoute><LazyRoute><RoyaltyContractPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/import" element={<RoyaltyRoute permission="manage_payments"><LazyRoute><RoyaltyImportPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/allocation" element={<RoyaltyRoute permission="manage_payments"><LazyRoute><RoyaltyUnallocatedPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/allocation/:category" element={<RoyaltyRoute permission="manage_payments"><LazyRoute><RoyaltyAllocationCategoryPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/royalties/refunds" element={<RoyaltyRoute permission="manage_payments"><LazyRoute><RoyaltyRefundsPage /></LazyRoute></RoyaltyRoute>} />
                <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                <Route path="/admin/notifications" element={<NotificationAdminPage />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/studio-metrics" element={<StudioMetricsRedirect />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/studio-metrics-grid" element={<WeeklyPlanGridPrototype />} />
                <Route path="/partners/:partnerNumber/studios/:studioId/plan-history" element={<PlanHistoryPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </>
          ) : (
            <>
              <Route path="/" element={
                (isInTelegram || hasAuthToken) ? <LandingPage /> : <Navigate to="/login" replace />
              } />
              <Route path="*" element={
                <Navigate to={(isInTelegram || hasAuthToken) ? "/" : "/login"} replace />
              } />
            </>
          )}
        </Routes>
      </AuthProvider>
    </Router>
  )
}

export default App
